/**
 * WP-V2-QC — stan misji (czysta logika, PLAN-V2 §7.2).
 * Kroki liczy klient; wypłata tylko serwer (questClient).
 * Rotacja 3/dzień — deterministycznie z daty (UTC YYYY-MM-DD).
 */
import {
  QUESTS,
  getQuestById,
  type QuestDef,
  type QuestGiver,
} from '@pieskowo/shared';

export type QuestStatus = 'idle' | 'offered' | 'active' | 'completed' | 'rewarded';

export type ActiveQuest = {
  questId: string;
  currentStep: number;
  steps: number;
  status: 'active' | 'completed' | 'rewarded';
};

export type QuestStateSnapshot = {
  /** UTC day key YYYY-MM-DD. */
  dayKey: string;
  /** 3 misje dnia (id). */
  dailyQuestIds: readonly string[];
  /** Oferta oczekująca (Tak/Później). */
  offeredQuestId: string | null;
  /** Aktywna misja lub null. */
  active: ActiveQuest | null;
  /** Odroczone dziś (Później) — nie pokazuje oferty ponownie tego dnia. */
  deferredIds: readonly string[];
  /** Ukończone dziś (klient). */
  completedIds: readonly string[];
  /** Odebrane nagrody dziś (po udanym POST). */
  rewardedIds: readonly string[];
};

export type QuestStateOptions = {
  /** Fake clock / Date — domyślnie Date.now. */
  now?: () => number;
  catalog?: readonly QuestDef[];
};

const DAILY_COUNT = 3;

/** UTC day string YYYY-MM-DD z timestampu ms. */
export function dayKeyFromMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Prosty hash string → uint32 (deterministyczny, bez crypto). */
export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Wybiera `count` unikalnych misji z katalogu na dany dzień.
 * Ta sama data UTC → ten sam zestaw (kolejność stabilna).
 */
export function pickDailyQuests(
  dayKey: string,
  catalog: readonly QuestDef[] = QUESTS,
  count: number = DAILY_COUNT,
): string[] {
  if (catalog.length === 0) return [];
  const n = Math.min(count, catalog.length);
  const indices = catalog.map((_, i) => i);
  // Fisher–Yates z seedowanym LCG
  let seed = hashString(`quests:${dayKey}`);
  const rand = (): number => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 0x100000000;
  };
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = indices[i]!;
    indices[i] = indices[j]!;
    indices[j] = tmp;
  }
  return indices.slice(0, n).map((i) => catalog[i]!.id);
}

export function createQuestState(opts: QuestStateOptions = {}): QuestStateSnapshot {
  const now = opts.now ?? (() => Date.now());
  const catalog = opts.catalog ?? QUESTS;
  const dayKey = dayKeyFromMs(now());
  return {
    dayKey,
    dailyQuestIds: pickDailyQuests(dayKey, catalog),
    offeredQuestId: null,
    active: null,
    deferredIds: [],
    completedIds: [],
    rewardedIds: [],
  };
}

/**
 * Odśwież dzień przy zmianie daty (np. po północy UTC / fake-clock).
 * Zachowuje stan tylko w ramach tego samego dayKey.
 */
export function syncDay(
  state: QuestStateSnapshot,
  opts: QuestStateOptions = {},
): QuestStateSnapshot {
  const now = opts.now ?? (() => Date.now());
  const catalog = opts.catalog ?? QUESTS;
  const dayKey = dayKeyFromMs(now());
  if (dayKey === state.dayKey) return state;
  return {
    dayKey,
    dailyQuestIds: pickDailyQuests(dayKey, catalog),
    offeredQuestId: null,
    active: null,
    deferredIds: [],
    completedIds: [],
    rewardedIds: [],
  };
}

/** Misje dnia dostępne do oferty (nie aktywne, nie deferred, nie completed). */
export function availableDailyQuests(
  state: QuestStateSnapshot,
  catalog: readonly QuestDef[] = QUESTS,
): QuestDef[] {
  const blocked = new Set<string>([
    ...state.deferredIds,
    ...state.completedIds,
    ...state.rewardedIds,
  ]);
  if (state.active) blocked.add(state.active.questId);
  return state.dailyQuestIds
    .map((id) => getQuestById(id) ?? catalog.find((q) => q.id === id))
    .filter((q): q is QuestDef => Boolean(q) && !blocked.has(q!.id));
}

/**
 * Proponuje misję (oferta Tak/Później).
 * Domyślnie pierwsza dostępna dzienna; opcjonalnie konkretne id.
 */
export function offerQuest(
  state: QuestStateSnapshot,
  questId?: string,
  catalog: readonly QuestDef[] = QUESTS,
): QuestStateSnapshot {
  if (state.active?.status === 'active') return state;
  if (state.offeredQuestId) return state;

  let id = questId;
  if (!id) {
    const avail = availableDailyQuests(state, catalog);
    id = avail[0]?.id;
  }
  if (!id) return state;

  const def = getQuestById(id) ?? catalog.find((q) => q.id === id);
  if (!def) return state;
  if (state.completedIds.includes(id) || state.rewardedIds.includes(id)) return state;
  if (state.deferredIds.includes(id)) return state;

  return { ...state, offeredQuestId: id };
}

/** Oferta od konkretnego dającego (misia/lola/kotek). */
export function offerFromGiver(
  state: QuestStateSnapshot,
  giver: QuestGiver,
  catalog: readonly QuestDef[] = QUESTS,
): QuestStateSnapshot {
  if (state.active?.status === 'active' || state.offeredQuestId) return state;
  const avail = availableDailyQuests(state, catalog).filter((q) => q.giver === giver);
  if (avail.length === 0) return state;
  return offerQuest(state, avail[0]!.id, catalog);
}

export function acceptOffer(state: QuestStateSnapshot): QuestStateSnapshot {
  const id = state.offeredQuestId;
  if (!id) return state;
  const def = getQuestById(id);
  if (!def) return { ...state, offeredQuestId: null };
  return {
    ...state,
    offeredQuestId: null,
    active: {
      questId: id,
      currentStep: 0,
      steps: def.steps,
      status: 'active',
    },
  };
}

export function deferOffer(state: QuestStateSnapshot): QuestStateSnapshot {
  const id = state.offeredQuestId;
  if (!id) return state;
  const deferred = state.deferredIds.includes(id)
    ? state.deferredIds
    : [...state.deferredIds, id];
  return {
    ...state,
    offeredQuestId: null,
    deferredIds: deferred,
  };
}

/**
 * Przyjmij misję bez oferty (np. E2E / dev).
 * Wymaga istnienia w katalogu; nie wymaga daily rotacji.
 */
export function forceAccept(
  state: QuestStateSnapshot,
  questId: string,
  catalog: readonly QuestDef[] = QUESTS,
): QuestStateSnapshot {
  const def = getQuestById(questId) ?? catalog.find((q) => q.id === questId);
  if (!def) return state;
  return {
    ...state,
    offeredQuestId: null,
    active: {
      questId: def.id,
      currentStep: 0,
      steps: def.steps,
      status: 'active',
    },
  };
}

export type StepResult = {
  state: QuestStateSnapshot;
  /** true gdy właśnie dobito steps. */
  justCompleted: boolean;
  progress: ActiveQuest | null;
};

/**
 * +1 krok aktywnej misji (tylko gdy questId pasuje).
 * Po osiągnięciu steps → status completed (wypłatę robi klient API).
 */
export function recordStep(state: QuestStateSnapshot, questId: string): StepResult {
  const active = state.active;
  if (!active || active.questId !== questId) {
    return { state, justCompleted: false, progress: active };
  }
  if (active.status !== 'active') {
    return { state, justCompleted: false, progress: active };
  }

  const nextStep = Math.min(active.currentStep + 1, active.steps);
  const done = nextStep >= active.steps;
  const nextActive: ActiveQuest = {
    ...active,
    currentStep: nextStep,
    status: done ? 'completed' : 'active',
  };
  const completedIds =
    done && !state.completedIds.includes(questId)
      ? [...state.completedIds, questId]
      : state.completedIds;

  return {
    state: {
      ...state,
      active: nextActive,
      completedIds,
    },
    justCompleted: done,
    progress: nextActive,
  };
}

/** Po udanym POST /api/quests/complete — oznacz rewarded i wyczyść active. */
export function markRewarded(state: QuestStateSnapshot, questId: string): QuestStateSnapshot {
  const rewardedIds = state.rewardedIds.includes(questId)
    ? state.rewardedIds
    : [...state.rewardedIds, questId];
  const active =
    state.active?.questId === questId
      ? { ...state.active, status: 'rewarded' as const }
      : state.active;
  return {
    ...state,
    active: active?.status === 'rewarded' ? null : active,
    rewardedIds,
    completedIds: state.completedIds.includes(questId)
      ? state.completedIds
      : [...state.completedIds, questId],
  };
}

/** Czy payload z szyny interact to krok tej misji (questId w evencie). */
export function questIdFromInteractPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  if (typeof p.questId === 'string' && p.questId.length > 0) return p.questId;
  return null;
}

export function getActiveDef(
  state: QuestStateSnapshot,
  catalog: readonly QuestDef[] = QUESTS,
): QuestDef | null {
  if (!state.active) return null;
  return getQuestById(state.active.questId) ?? catalog.find((q) => q.id === state.active!.questId) ?? null;
}
